import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BlockNoteSchema, defaultBlockSpecs, defaultStyleSpecs } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { FormattingToolbar, useCreateBlockNote } from '@blocknote/react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import MarkdownImporter from '@components/Markdown/MarkdownImporter';
import type { ModalComponentProps } from '@components/modals/constants';
import { useSubjectLanguageVariants } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { parseNoteToBlocks, serializeBlocks } from '@utils/blocknote';

import { useGetTreatmentPlanQuery, useSaveTreatmentPlanMutation } from '../store/api';

import type { TreatmentPlanEditModalProps } from './PatientTreatmentPlanEditModal.constants';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

const {
  codeBlock: _codeBlock,
  audio: _audio,
  video: _video,
  image: _image,
  file: _file,
  ...blockSpecs
} = defaultBlockSpecs;
const { code: _code, ...styleSpecs } = defaultStyleSpecs;
const treatmentPlanSchema = BlockNoteSchema.create({ blockSpecs, styleSpecs });

export const PatientTreatmentPlanEditModal: React.FC<ModalComponentProps> = ({
  modalProps,
  closeModal,
}) => {
  const {
    patientId,
    source = 'unknown',
  } = modalProps as TreatmentPlanEditModalProps;

  const { singular } = useSubjectLanguageVariants();
  const { patientContextBar: contextBarTracking } = useEventTracking();
  const { data: treatmentPlan, isLoading: isFetchingPlan } = useGetTreatmentPlanQuery({ patientId });

  const [treatmentPlanId, setTreatmentPlanId] = useState<string | null>(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const pendingMarkdownRef = useRef<string | null>(null);
  const isLoadingContent = useRef(false);
  const triggerAutoSaveRef = useRef<((md: string) => void) | null>(null);

  const editor = useCreateBlockNote({ schema: treatmentPlanSchema });
  const [saveMutation] = useSaveTreatmentPlanMutation();

  const initializedRef = useRef(false);

  useEffect(() => {
    if (isFetchingPlan || initializedRef.current) return;
    initializedRef.current = true;

    const loadContent = async () => {
      isLoadingContent.current = true;
      const stored = treatmentPlan?.markdown_content || '';
      const blocks = await parseNoteToBlocks(editor, stored);
      if (blocks.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        editor.replaceBlocks(editor.document, blocks);
      }
      setTreatmentPlanId(treatmentPlan?.treatment_plan_id || null);
      setHasLocalChanges(false);
      pendingMarkdownRef.current = null;
      requestAnimationFrame(() => {
        isLoadingContent.current = false;
      });
    };
    loadContent();
  }, [isFetchingPlan, treatmentPlan, editor]);

  const performSave = useCallback(
    async (markdownToSave: string) => {
      try {
        const result = await saveMutation({
          patientId,
          markdownContent: markdownToSave,
          treatmentPlanId,
        }).unwrap();
        if (result?.treatment_plan_id && result.treatment_plan_id !== treatmentPlanId) {
          setTreatmentPlanId(result.treatment_plan_id);
        }
        setHasLocalChanges(false);
        pendingMarkdownRef.current = null;
      } catch (error) {
        console.error('Failed to save treatment plan:', error);
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'treatment_plan',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [patientId, treatmentPlanId, saveMutation, contextBarTracking, source],
  );

  const { triggerAutoSave, saveImmediately, status, lastSavedAt } = useAutoSave({
    onSave: performSave,
    debounceMs: 2000,
    enabled: hasLocalChanges,
    flushOnUnmount: true,
  });
  triggerAutoSaveRef.current = triggerAutoSave;

  const handleEditorChange = useCallback(() => {
    if (isLoadingContent.current) return;
    const serialized = serializeBlocks(editor.document);
    setHasLocalChanges(true);
    pendingMarkdownRef.current = serialized;
    triggerAutoSave(serialized);
  }, [editor, triggerAutoSave]);

  const handleBlur = useCallback(() => {
    if (pendingMarkdownRef.current !== null) {
      saveImmediately(pendingMarkdownRef.current);
      pendingMarkdownRef.current = null;
    }
  }, [saveImmediately]);

  const handleClose = useCallback(() => {
    if (status === 'saving') return;
    contextBarTracking.editModalClosed({
      patient_id: patientId,
      data_type: 'treatment_plan',
      had_changes: hasLocalChanges,
      source,
    });
    if (hasLocalChanges && pendingMarkdownRef.current !== null) {
      saveImmediately(pendingMarkdownRef.current);
      pendingMarkdownRef.current = null;
    }
    closeModal();
  }, [status, hasLocalChanges, saveImmediately, closeModal, patientId, source, contextBarTracking]);

  const handleImport = useCallback(() => {
    contextBarTracking.importClicked({
      patient_id: patientId,
      data_type: 'treatment_plan',
      source,
    });
    setImportOpen(true);
  }, [patientId, source, contextBarTracking]);

  const handleImportSuccess = useCallback(
    async (markdown: string) => {
      try {
        const blocks = await editor.tryParseMarkdownToBlocks(markdown);
        editor.replaceBlocks(editor.document, blocks);
        const serialized = serializeBlocks(editor.document);
        setHasLocalChanges(true);
        pendingMarkdownRef.current = serialized;
        triggerAutoSaveRef.current?.(serialized);
        setImportOpen(false);
        contextBarTracking.importCompleted({
          patient_id: patientId,
          data_type: 'treatment_plan',
          source,
        });
      } catch (error) {
        contextBarTracking.importFailed({
          patient_id: patientId,
          data_type: 'treatment_plan',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
      }
    },
    [editor, patientId, source, contextBarTracking],
  );

  return (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Treatment Plan
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleImport}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8125rem',
              }}
            >
              Import
            </Button>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              disabled={status === 'saving'}
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box
          onBlur={handleBlur}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            '& .bn-container': {
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column-reverse',
              height: '100%',
              overflow: 'auto !important',
            },
            '& .bn-editor': {
              flex: 1,
              padding: '16px',
              overflow: 'visible !important',
            },
          }}
        >
          <BlockNoteView
            editor={editor}
            editable
            theme="light"
            onChange={handleEditorChange}
            formattingToolbar={false}
            sideMenu={false}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '& .bn-formatting-toolbar': {
                  border: 'none',
                  borderRadius: 0,
                  boxShadow: 'none',
                  padding: '4px 16px',
                  minHeight: 36,
                },
              }}
            >
              <FormattingToolbar />
            </Box>
          </BlockNoteView>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <AutosaveStatus
          status={status}
          isDirty={hasLocalChanges}
          lastSavedAt={lastSavedAt}
          verbose
        />
        <Button
          onClick={handleClose}
          variant="contained"
          disabled={status === 'saving' || hasLocalChanges}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          Done
        </Button>
      </DialogActions>

      <MarkdownImporter
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImportSuccess={handleImportSuccess}
        endpoint="/treatment_plan"
        contentFieldName="text"
        additionalParams={{ patient_id: patientId }}
        title="Import Treatment Plan"
        description="Paste text or drag a file to import a treatment plan. Supported file types: .pdf, .docx, .jpg, .jpeg, .png"
        acceptedFileTypes=".pdf,.docx,.jpg,.jpeg,.png"
        warningText={
          treatmentPlan && treatmentPlan.markdown_content.trim().length > 0
            ? `Note: This import will overwrite the existing treatment plan for this ${singular}.`
            : undefined
        }
      />
    </>
  );
};

PatientTreatmentPlanEditModal.displayName = 'PatientTreatmentPlanEditModal';
